import React, { useContext } from 'react'
import cn from 'classnames'
import { FixedSpacer } from '@grammarly/badger-ui'

import {
  ImageContext,
  heroSectionResponsiveImageProps,
  grammarlyAtWork25HeroImageProps,
  GrammarlyAtWork25Context,
} from '../../../contexts/image-context'
import { CtaButtonMetadataProvider, DefaultCtaButtonContext } from '../../../contexts/cta-button-context'
import {
  SectionHeroLayoutDir,
  TypeSectionHero,
  CtaDescriptionPosition,
  CtaDescriptionWidth,
  TwoColumnProportion,
  CollapseToSingleColumnBreakpoints,
  SectionHeroDynamicRenderAs,
} from '../../../lib/types'
import { useBrowserService } from '../../../lib/hooks'
import { TwoColumnLayout } from '../../layout/two-column'
import { PageSection } from '../../page-section'
import { ContentProps, ContentRenderer, EntryContentProps } from '../../renderer'
import { HeroTitle } from './title'
import { SingleColumnLayout } from '../../layout/single-column'
import { AlignmentContext, DefaultAlignment } from '../../../contexts/alignment-context'
import { usePageTheme } from '../../../contexts/page-theme-context'
import { SplineLoadingProvider } from '@lib/hooks/useSplineLoading'
import { SectionContext } from '../../../contexts/section-context'

import styles from './hero.module.scss'
import grammarlyAtWork25Styles from '../../dynamic/grammarly-at-work-25/grammarly-at-work-25.module.scss'

const HeroSectionContent: React.FC<EntryContentProps<TypeSectionHero>> = ({ structure }) => {
  const {
    fields: { layoutDirection, image, dynamicRenderAs },
  } = structure

  const isVertical = layoutDirection === SectionHeroLayoutDir.Vertical
  const isVerticalWide = layoutDirection === SectionHeroLayoutDir.VerticalWide
  const isLayoutReversed = layoutDirection === SectionHeroLayoutDir.Reversed
  const { isMobile } = useBrowserService()
  const { isGrouped } = useContext(SectionContext)
  const { pageWidth } = usePageTheme()

  const shouldUseSplineWrapper = dynamicRenderAs?.includes(SectionHeroDynamicRenderAs.Bts2025AnimatedHero)
  const isGrammarlyAtWork25 = dynamicRenderAs?.includes(SectionHeroDynamicRenderAs.GrammarlyAtWork25)
  const isBtsAnimatedHero = dynamicRenderAs?.includes(SectionHeroDynamicRenderAs.Bts2025AnimatedHero)

  // Use enhanced image properties for GrammarlyAtWork25 hero, but keep original for BTS
  const imageContextValue = isGrammarlyAtWork25 ? grammarlyAtWork25HeroImageProps : heroSectionResponsiveImageProps

  // Helper function to render image with conditional wrapper
  const renderImage = () => {
    // BTS animated hero needs simple rendering without wrapper divs
    if (isBtsAnimatedHero) {
      return <ContentRenderer structure={image} />
    }

    // Grammarly at Work 25 needs special wrapper classes
    return (
      <div
        className={cn({
          [styles.grammarlyAtWork25ImageWrapper]: isGrammarlyAtWork25,
          [grammarlyAtWork25Styles.grammarlyImageContainer]: isGrammarlyAtWork25,
        })}
      >
        <ContentRenderer structure={image} />
      </div>
    )
  }

  const content = (
    <GrammarlyAtWork25Context.Provider value={{ isGrammarlyAtWork25: !!isGrammarlyAtWork25 }}>
      <ImageContext.Provider value={imageContextValue}>
        <CtaButtonMetadataProvider
          value={{
            ...DefaultCtaButtonContext,
            descriptionPosition: CtaDescriptionPosition.Left,
            descriptionWidth: CtaDescriptionWidth.Auto,
            showCtaDescription: false,
          }}
        >
          {isVertical || isVerticalWide ? (
            <SingleColumnLayout
              className={cn({
                [styles.singleSectionWide]: isVerticalWide,
                [styles.singleSectionWideGrouped]: isGrouped && isVerticalWide,
              })}
            >
              <AlignmentContext.Provider value={DefaultAlignment}>
                <HeroTitle structure={structure} />
                {!(isVerticalWide && isMobile) && <FixedSpacer multiplier={isMobile ? 3 : 5} />}
                {renderImage()}
              </AlignmentContext.Provider>
            </SingleColumnLayout>
          ) : (
            <TwoColumnLayout
              leftContent={isLayoutReversed ? renderImage() : <HeroTitle structure={structure} />}
              rightContent={isLayoutReversed ? <HeroTitle structure={structure} /> : renderImage()}
              columnsProportion={TwoColumnProportion.OneToOne}
              collapseToSingleColumnBreakpoint={CollapseToSingleColumnBreakpoints.medium}
              className={cn(pageWidth === '1280' && styles.twoColRedesign24)}
            />
          )}
        </CtaButtonMetadataProvider>
      </ImageContext.Provider>
    </GrammarlyAtWork25Context.Provider>
  )

  if (shouldUseSplineWrapper) {
    return (
      <SplineLoadingProvider>
        <div style={{ position: 'relative' }}>{content}</div>
      </SplineLoadingProvider>
    )
  }

  return content
}

export const HeroSection: React.FC<ContentProps<TypeSectionHero>> = ({ className, structure }) => {
  const {
    sys,
    fields: { internalName, background },
  } = structure

  return (
    <PageSection internalName={internalName} sysStructure={sys} className={className} background={background}>
      <HeroSectionContent structure={structure} />
    </PageSection>
  )
}
